import GridDrawer from '@/components/common/GridDrawer';
import { useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import ToolTabs from '@/app/common/components/ToolTabs';
import { toolRoutes } from '@/routes/siteMaps';
import RouteHelperAlerts from '@/components/common/RouteHelperAlerts';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import DataLoadError from '@/components/errors/DataLoadError';
import {
  shouldShowDataLoadErrorFromQueries,
  refetchQueries,
  isAnyQueryFetching,
  isNetworkApiError
} from '@/app/common/utils/apiErrorUtils';
import ToolsBreadcrumb from '../common/components/ToolsBreadcrumb';
import KeywordProgressDrawer from './components/keywords/drawers/KeywordProgressDrawer';
import RanksHistoryDrawer from './components/keywords/drawers/RanksHistoryDrawer';
import RelatedKeywordsDrawer from './components/keywords/drawers/RelatedKeywordsDrawer';
import GoogleResultHistoryDrawer from './components/keywords/drawers/GoogleResultHistoryDrawer';
import CannibalizationsModal from './components/keywords/drawers/CannibalizationsModal';
import AccessLevelFailed from '@/app/common/components/AccessLevelFailed';
import ConflictModal from './components/keywords/drawers/ConflictModal';
import useDomain from '@/hooks/useDomain';
import NoteModal from './components/keywords/drawers/NoteModal';
import { useGetTariffInfoQuery } from './api/keywordsApi';
import AiModal from './components/keywords/drawers/AiModal';
import TrackerInactiveState from './components/TrackerInactiveState';
import { isTrackerTariffInactive } from './utils/trackerTariff';

const KeywordTrackerMain = () => {
  const { domain, domain_slug } = useDomain();
  const { data: userInfo, isLoading, isError, error, refetch, isFetching } = useGetUserInfoQuery();
  //
  const routes = toolRoutes.children.find(item => item.name === 'ردیاب کلمات');
  const location = useLocation();
  const [breadcrumbData, setBreadcrumbData] = useState([]);
  const navItems = routes;

  const activePage = useMemo(
    () =>
      navItems?.children?.find(route => {
        if (route.to === '/keyword-tracker/insight') return false;
        const to = `${route.to}/${domain_slug}/${domain}`;
        return location.pathname === to || location.pathname.startsWith(`${to}/`);
      }),
    [navItems, location.pathname, domain_slug, domain]
  );
  const {
    data: trackerInfo,
    isFetching: isFetchingTrackerInfo,
    isError: isErrorFetchingTrackerInfo,
    error: trackerInfoError,
    refetch: refetchTrackerInfo
  } = useGetTariffInfoQuery(domain_slug);
  const isTrackerInactive =
    !isFetchingTrackerInfo && isTrackerTariffInactive(trackerInfo);

  if (
    shouldShowDataLoadErrorFromQueries(
      { isError, error },
      { isError: isErrorFetchingTrackerInfo, error: trackerInfoError }
    )
  ) {
    return (
      <DataLoadError
        onRetry={() => refetchQueries(refetch, refetchTrackerInfo)}
        isRetrying={isAnyQueryFetching(
          { isFetching },
          { isFetching: isFetchingTrackerInfo }
        )}
        isNetworkError={
          isNetworkApiError(error) || isNetworkApiError(trackerInfoError)
        }
      />
    );
  }

  const trackerNavTabs = (
    <ToolTabs
      items={(navItems?.children ?? []).map(route => {
        const soon = route.to === '/keyword-tracker/insight';
        const to = `${route.to}/${domain_slug}/${domain}`;
        return {
          key: route.to,
          label: route.name,
          to,
          disabled: Boolean(route.disabled) || soon,
          tooltip: soon ? 'به زودی' : undefined,
          active:
            !soon &&
            (location.pathname === to || location.pathname.startsWith(`${to}/`))
        };
      })}
    />
  );

  const trackerPageContent = (
    <>
      {trackerNavTabs}
      {activePage && <RouteHelperAlerts route={activePage} />}
      <GridDrawer />
      <Outlet context={[breadcrumbData, setBreadcrumbData]} />
    </>
  );

  if (!isLoading) {
    return (
      <>
        {!isFetchingTrackerInfo && trackerInfo.data.tariff.expired_panel ? (
          <AccessLevelFailed
            tool_name={'مدیریت کلمات'}
            accessData={
              !isErrorFetchingTrackerInfo
                ? {
                    data: { expired_panel: true }
                  }
                : trackerInfoError.data
            }
          />
        ) : (
          <>
            <ToolsBreadcrumb
              parentName="ردیاب کلمات"
              parentUrl={`/keyword-tracker/keywords/${domain_slug}/${domain}`}
              childs={breadcrumbData}
            />
            {isTrackerInactive ? (
              <TrackerInactiveState>{trackerPageContent}</TrackerInactiveState>
            ) : (
              trackerPageContent
            )}
            <KeywordProgressDrawer />
            <RanksHistoryDrawer />
            <RelatedKeywordsDrawer />
            <NoteModal />
            <AiModal />
            <GoogleResultHistoryDrawer />
            <CannibalizationsModal />
            <ConflictModal />
          </>
        )}
      </>
    );
  }
};

export default KeywordTrackerMain;
